import { useEffect, useState, useCallback, useRef } from "react";
import {
  FiDatabase,
  FiDownload,
  FiUpload,
  FiAlertTriangle,
  FiCheck,
  FiLoader,
  FiFile,
  FiRefreshCw,
} from "react-icons/fi";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CardSkeleton } from "@/components/common";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

type TableInfo = {
  name: string;
  displayName: string;
  rowCount: number;
  description: string;
  sensitive?: boolean;
};

type ExportFormat = "json" | "csv";

const TABLE_METADATA: Record<string, { displayName: string; description: string; sensitive?: boolean }> = {
  admin_users: { displayName: "Admin Users", description: "Admin accounts and roles", sensitive: true },
  customers: { displayName: "Customers", description: "Customer/prospect accounts" },
  subscribers: { displayName: "Subscribers", description: "Email newsletter subscribers" },
  leads: { displayName: "Leads", description: "Lead/enquiry submissions" },
  testimonials: { displayName: "Testimonials", description: "Customer testimonials" },
  posts: { displayName: "Blog Posts", description: "Blog content" },
  post_categories: { displayName: "Blog Categories", description: "Blog post categories" },
  lenders: { displayName: "Lenders", description: "Mortgage lender information" },
  rates: { displayName: "Rates", description: "Interest rate products" },
  cash_rate_history: { displayName: "Cash Rate History", description: "RBA cash rate history" },
  rba_news: { displayName: "RBA News", description: "RBA media releases cache" },
  campaigns: { displayName: "Email Campaigns", description: "Marketing email campaigns" },
  campaign_sends: { displayName: "Campaign Sends", description: "Individual campaign email sends" },
  campaign_events: { displayName: "Campaign Events", description: "Email open/click tracking" },
  email_automations: { displayName: "Email Automations", description: "Automated email rules" },
  scheduled_emails: { displayName: "Scheduled Emails", description: "Email send queue" },
  automation_logs: { displayName: "Automation Logs", description: "Automation run history" },
  documents: { displayName: "Documents", description: "Customer document metadata" },
  fact_finders: { displayName: "Fact Finders", description: "Financial questionnaires", sensitive: true },
  sessions: { displayName: "Sessions", description: "Active user sessions", sensitive: true },
  auth_codes: { displayName: "Auth Codes", description: "Login OTP codes", sensitive: true },
};

export function DatabasePage() {
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const [exportFormat, setExportFormat] = useState<ExportFormat>("json");
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showImportWarning, setShowImportWarning] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<{
    tables: string[];
    totalRows: number;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchTables = useCallback(async () => {
    try {
      const response = await fetch("/api/admin/database/tables", {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json() as { tables: { name: string; rowCount: number }[] };
        const tablesWithMeta = data.tables.map((t) => ({
          ...t,
          displayName: TABLE_METADATA[t.name]?.displayName || t.name,
          description: TABLE_METADATA[t.name]?.description || "",
          sensitive: TABLE_METADATA[t.name]?.sensitive || false,
        }));
        setTables(tablesWithMeta);
      }
    } catch (error) {
      console.error("Failed to fetch tables:", error);
      toast.error("Failed to load database information");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  const toggleTable = (tableName: string) => {
    setSelectedTables((prev) =>
      prev.includes(tableName)
        ? prev.filter((t) => t !== tableName)
        : [...prev, tableName]
    );
  };

  const selectAll = () => {
    if (selectedTables.length === tables.length) {
      setSelectedTables([]);
    } else {
      setSelectedTables(tables.map((t) => t.name));
    }
  };

  const handleExport = async () => {
    if (selectedTables.length === 0) {
      toast.error("Please select at least one table to export");
      return;
    }

    setIsExporting(true);
    try {
      const response = await fetch("/api/admin/database/export", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tables: selectedTables,
          format: exportFormat,
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const timestamp = new Date().toISOString().slice(0, 10);
        const filename = `pathfinancial-backup-${timestamp}.${exportFormat === "json" ? "json" : "zip"}`;

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        toast.success(`Exported ${selectedTables.length} tables successfully`);
      } else {
        const errorData = await response.json() as { error?: string };
        toast.error(errorData.error || "Export failed");
      }
    } catch (err) {
      console.error("Export failed:", err);
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Parse a multi-table CSV export (with "# TABLE: name" separators)
   * or a single-table CSV into { tableName: rows[] } structure
   */
  const parseCSVBackup = (text: string): Record<string, Record<string, string>[]> => {
    const result: Record<string, Record<string, string>[]> = {};
    const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");

    let currentTable = "";
    let headers: string[] = [];

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;

      // Check for table separator
      const tableMatch = trimmed.match(/^#\s*TABLE:\s*(.+)$/i);
      if (tableMatch) {
        currentTable = tableMatch[1].trim();
        headers = [];
        if (!result[currentTable]) result[currentTable] = [];
        continue;
      }

      // Parse CSV line respecting quotes
      const parseLine = (raw: string): string[] => {
        const fields: string[] = [];
        let current = "";
        let inQuotes = false;
        for (let k = 0; k < raw.length; k++) {
          const ch = raw[k];
          if (inQuotes) {
            if (ch === '"') {
              if (k + 1 < raw.length && raw[k + 1] === '"') {
                current += '"';
                k++;
              } else {
                inQuotes = false;
              }
            } else {
              current += ch;
            }
          } else if (ch === '"') {
            inQuotes = true;
          } else if (ch === ",") {
            fields.push(current);
            current = "";
          } else {
            current += ch;
          }
        }
        fields.push(current);
        return fields;
      };

      const values = parseLine(trimmed);

      // If no headers yet for this table, treat line as headers
      if (headers.length === 0) {
        headers = values.map((h) => h.trim());
        // If no table name from separator, try to infer or use "imported"
        if (!currentTable) {
          currentTable = "imported";
          result[currentTable] = [];
        }
        continue;
      }

      // Build row object
      const row: Record<string, string> = {};
      for (let j = 0; j < headers.length; j++) {
        row[headers[j]] = (values[j] || "").trim();
      }
      result[currentTable].push(row);
    }

    return result;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportFile(file);

    const isCSV = file.name.toLowerCase().endsWith(".csv");

    // Preview the import file
    try {
      const text = await file.text();

      if (isCSV) {
        const parsed = parseCSVBackup(text);
        const tableNames = Object.keys(parsed);
        const totalRows = tableNames.reduce((sum, t) => sum + parsed[t].length, 0);

        if (tableNames.length === 0 || totalRows === 0) {
          toast.error("No valid data found in CSV file");
          setImportFile(null);
          return;
        }

        setImportPreview({ tables: tableNames, totalRows });
        setShowImportWarning(true);
      } else {
        const data = JSON.parse(text);
        const tableNames = Object.keys(data);
        const totalRows = tableNames.reduce((sum, t) => sum + (Array.isArray(data[t]) ? data[t].length : 0), 0);
        setImportPreview({ tables: tableNames, totalRows });
        setShowImportWarning(true);
      }
    } catch {
      toast.error(isCSV ? "Invalid CSV file" : "Invalid JSON file");
      setImportFile(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;

    setIsImporting(true);
    setShowImportWarning(false);

    try {
      const text = await importFile.text();
      const isCSV = importFile.name.toLowerCase().endsWith(".csv");
      const data = isCSV ? parseCSVBackup(text) : JSON.parse(text);

      const response = await fetch("/api/admin/database/import", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data }),
      });

      if (response.ok) {
        const result = await response.json() as { tablesImported: number };
        toast.success(`Imported ${result.tablesImported} tables successfully`);
        fetchTables(); // Refresh table counts
      } else {
        const errorData = await response.json() as { error?: string };
        toast.error(errorData.error || "Import failed");
      }
    } catch (err) {
      console.error("Import failed:", err);
      toast.error("Import failed");
    } finally {
      setIsImporting(false);
      setImportFile(null);
      setImportPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const totalRows = tables.reduce((sum, t) => sum + t.rowCount, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl font-medium text-slate-900">
          Database Management
        </h1>
        <p className="text-muted-foreground mt-1">
          Export, import, and backup your D1 database
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FiDatabase className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{tables.length}</p>
                <p className="text-sm text-muted-foreground">Tables</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FiFile className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{totalRows.toLocaleString()}</p>
                <p className="text-sm text-muted-foreground">Total Rows</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FiCheck className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{selectedTables.length}</p>
                <p className="text-sm text-muted-foreground">Selected for Export</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Export Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FiDownload className="w-5 h-5" />
              Export Database
            </CardTitle>
            <CardDescription>
              Select tables to export as JSON or CSV backup
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Format Selection */}
            <div>
              <label className="text-sm font-medium mb-2 block">Export Format</label>
              <div className="flex gap-2">
                <Button
                  variant={exportFormat === "json" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setExportFormat("json")}
                >
                  JSON
                </Button>
                <Button
                  variant={exportFormat === "csv" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setExportFormat("csv")}
                >
                  CSV (ZIP)
                </Button>
              </div>
            </div>

            {/* Table Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Select Tables</label>
                <Button variant="ghost" size="sm" onClick={selectAll}>
                  {selectedTables.length === tables.length ? "Deselect All" : "Select All"}
                </Button>
              </div>

              {isLoading ? (
                <div className="space-y-2">
                  <CardSkeleton />
                  <CardSkeleton />
                  <CardSkeleton />
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                  {tables.map((table) => (
                    <div
                      key={table.name}
                      onClick={() => toggleTable(table.name)}
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedTables.includes(table.name)
                          ? "bg-primary/10 border-primary"
                          : "bg-muted/50 border-transparent hover:bg-muted"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            selectedTables.includes(table.name)
                              ? "bg-primary border-primary text-primary-foreground"
                              : "border-muted-foreground/30"
                          }`}
                        >
                          {selectedTables.includes(table.name) && (
                            <FiCheck className="w-3 h-3" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{table.displayName}</p>
                          <p className="text-xs text-muted-foreground">{table.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {table.sensitive && (
                          <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
                            Sensitive
                          </Badge>
                        )}
                        <Badge variant="secondary" className="text-xs">
                          {table.rowCount.toLocaleString()} rows
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Export Button */}
            <Button
              onClick={handleExport}
              disabled={isExporting || selectedTables.length === 0}
              className="w-full"
            >
              {isExporting ? (
                <>
                  <FiLoader className="w-4 h-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <FiDownload className="w-4 h-4 mr-2" />
                  Export {selectedTables.length} Table{selectedTables.length !== 1 ? "s" : ""}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Import Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FiUpload className="w-5 h-5" />
              Import / Restore Database
            </CardTitle>
            <CardDescription>
              Import data from a JSON or CSV backup file
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Warning Banner */}
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <FiAlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Warning: Destructive Operation</p>
                <p className="text-sm text-amber-700 mt-1">
                  Importing data will overwrite existing records in the selected tables.
                  Make sure to export a backup first.
                </p>
              </div>
            </div>

            {/* File Input */}
            <div>
              <label className="text-sm font-medium mb-2 block">Select Backup File</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,.csv"
                onChange={handleFileSelect}
                className="block w-full text-sm text-muted-foreground
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-medium
                  file:bg-primary file:text-primary-foreground
                  hover:file:bg-primary/90
                  file:cursor-pointer cursor-pointer"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Supports JSON and CSV backup files
              </p>
            </div>

            {/* Quick Actions */}
            <div className="border-t pt-4">
              <label className="text-sm font-medium mb-2 block">Quick Actions</label>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => {
                    setSelectedTables(tables.map((t) => t.name));
                    setExportFormat("json");
                  }}
                >
                  <FiDatabase className="w-4 h-4 mr-2" />
                  Select All Tables for Full Backup
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={fetchTables}
                >
                  <FiRefreshCw className="w-4 h-4 mr-2" />
                  Refresh Table Information
                </Button>
              </div>
            </div>

            {/* Info */}
            <div className="text-sm text-muted-foreground bg-muted/50 p-4 rounded-lg">
              <p className="font-medium mb-2">Backup Best Practices:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Export a full backup before making major changes</li>
                <li>Store backups in a secure, separate location</li>
                <li>Test restores periodically to ensure data integrity</li>
                <li>Sensitive tables contain encrypted data (fact finders)</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Import Confirmation Dialog */}
      <Dialog open={showImportWarning} onOpenChange={setShowImportWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <FiAlertTriangle className="w-5 h-5" />
              Confirm Database Import
            </DialogTitle>
            <DialogDescription className="pt-2">
              You are about to import data that will overwrite existing records.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {importPreview && (
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm font-medium mb-2">Import Summary:</p>
              <ul className="text-sm space-y-1">
                <li><strong>Tables:</strong> {importPreview.tables.length}</li>
                <li><strong>Total Rows:</strong> {importPreview.totalRows.toLocaleString()}</li>
                <li className="text-xs text-muted-foreground mt-2">
                  Tables: {importPreview.tables.join(", ")}
                </li>
              </ul>
            </div>
          )}

          <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <FiAlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">
              Existing data in these tables will be <strong>permanently deleted</strong> and replaced with the imported data.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowImportWarning(false);
                setImportFile(null);
                setImportPreview(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleImport}
              disabled={isImporting}
            >
              {isImporting ? (
                <>
                  <FiLoader className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <FiUpload className="w-4 h-4 mr-2" />
                  Import & Overwrite
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
